import { DurationUnit } from "~/enums/durationUnit";
import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
import {
  ConditionalRuleBuiltInValueType,
  computedConditionalRuleBuiltInValueTypeName,
  typeForConditionalRuleBuiltInValue,
} from "~/models/conditionalRules/conditionalRuleBuiltInValueType";
import Selected from "~/state/modules/selected";
import TrackedModel from "~/models/tracked/trackedModel";
import { ComparisonType } from "./comparisonType";
import ConditionalRuleSet from "./conditionalRuleSet";
import { DatetimeComparison } from "./datetimeComparison";
import { InstanceCountComparison } from "./instanceCountComparison";
import { MultiChoiceComparison } from "./multiChoiceComparison";
import { NumericComparison } from "./numericComparison";
import IConditionalRuleReferencedControl from "./referencedControl";
import { SingleChoiceComparison } from "./singleChoiceComparison";
import { TargetType } from "./targetType";
import { TextComparison } from "./textComparison";
import {AccountRelationComparison} from "~/models/conditionalRules/accountRelationComparison";
import ObjectModelRelationDefinition from "~/models/objectModelRelations/objectModelRelationDefinition";
import {NumberHelper} from "~/helpers/numberHelper";
import {StringHelper} from "~/helpers/stringHelper";
import {AccountRelationUserComparison} from "~/models/conditionalRules/accountRelationUserComparison";
import {Logger} from "~/services/logger";
import {RecordComparison} from '~/models/conditionalRules/recordComparison'
import {DateHelper} from '~/helpers/dateHelper'
import {BooleanComparison} from "~/models/conditionalRules/booleanComparison";
import ListView from "~/models/listViews/listView";
import BuiltInReferencedValue from "~/models/conditionalRules/builtInReferencedValue";

export type TConditionalRule = ConditionalRule;

export default abstract class ConditionalRule extends TrackedModel {
  referenced_control_id: string = null;
  target_type: TargetType = null;
  comparison_type: ComparisonType = null;
  comparison: number = null;
  value: any = null;
  sort_order: number;
  value_duration_unit: DurationUnit;
  is_reference: boolean;
  date_type: string = "static"
  relative_date_type: string = "last"
  relative_date: string = "monday"

  abstract get referencedControl(): IConditionalRuleReferencedControl;

  abstract getValueString(id: string): string;

  toString(): string {
    return `${this.referenceControlNameString} ${this.comparisonString} ${this.valueString()}`;
  }

  /**
   * @see {ConditionalRuleSet#toHtmlString}
   */
  toHtmlString(dataSource = null): string {
    return `<strong>${this.referenceControlNameString}</strong> ${this.comparisonString} <strong>${this.valueString(dataSource)}</strong>`;
  }

  resetComparison(): void {
    this.target_type = TargetType.value;
    switch (this.comparison_type) {
      case ComparisonType.instanceCount:
        this.comparison = InstanceCountComparison.hasCountEqualTo;
        break;
      case ComparisonType.numeric:
        this.comparison = NumericComparison.isEqualTo;
        break;
      case ComparisonType.singleChoice:
        this.comparison = SingleChoiceComparison.is;
        break;
      case ComparisonType.multiChoice:
        this.comparison = MultiChoiceComparison.includes;
        break;
      case ComparisonType.accountRelation:
        this.comparison = AccountRelationComparison.is;
        break;
      case ComparisonType.record:
        this.comparison = RecordComparison.isPresent;
        break;
      case ComparisonType.accountRelationUser:
        this.comparison = AccountRelationUserComparison.is;
        break;
      default:
        this.comparison = TextComparison.is;
        break;
    }
  }

  resetValue(): void {
    this.value = null;
  }

  setComparisonType(value): void {
    this.comparison_type = value;
    this.resetComparison();
    this.resetValue();
  }

  get referenceControlName(): string {
    const referencedControl = this.referencedControl;
    const name = referencedControl ? referencedControl.referenceNameOrDefault : null;
    if (this.target_type === TargetType.count) {
      return `${name}'s count of values`;
    } else {
      return name;
    }
  }

  matchesDependenciesFor(controlId: string): boolean {
    return this.referenced_control_id === controlId || this.value === controlId;
  }

  get isComplete(): boolean {
    let complete

    if (this.isDatetimeWithinRule) {
      complete = (this.value !== null) && (this.value_duration_unit !== null)
    } else if (this.isRelativeDateRule) {
      complete = this.relative_date_type !== null && this.relative_date !== null
    } else {
      complete = !!this.value || !this.isValueRequired;
    }

    return complete
  }

  get isValid(): boolean {
    if (!this.isComplete) return false;

    if (this.hasChoiceComparisonOptionValue && this.isValueRequired) {
      return !!this.value && !!this.referencedControl.getChoiceComparisonOptions().find(o => o.client_id === this.value);
    }

    return true;
  }

  get isDatetimeWithinRule(): boolean {
    return (this.comparison_type === ComparisonType.datetime) && (this.comparison === DatetimeComparison.isWithin)
  }

  get isRelativeDateRule(): boolean {
    return this.comparison_type === ComparisonType.datetime && this.date_type === "relative"
  }

  get calculatedComparisonType(): ComparisonType {
    if (this.comparison_type === ComparisonType.multiChoice && this.target_type === TargetType.count) {
      return ComparisonType.numeric;
    }
    return this.comparison_type;
  }

  get referenceControlNameString(): string {
    if (this.builtInValueType){
      return computedConditionalRuleBuiltInValueTypeName(this.builtInValueType, this);
    }
    else {
      if (this.referenceControlName === null || this.referenceControlName === undefined) {
        return '';
      } else {
        return this.referenceControlName;
      }
    }
  }

  get comparisonIsUpdated(): boolean{
      switch (this.calculatedComparisonType) {
        case ComparisonType.text:
          return this.comparison === TextComparison.isUpdated;
        case ComparisonType.singleChoice:
          return this.comparison === SingleChoiceComparison.isUpdated;
        case ComparisonType.multiChoice:
          return this.comparison === MultiChoiceComparison.isUpdated;
        case ComparisonType.numeric:
          return this.comparison ===  NumericComparison.isUpdated;
        case ComparisonType.instanceCount:
          return this.comparison === InstanceCountComparison.isUpdated;
        case ComparisonType.datetime:
          return this.comparison === DatetimeComparison.isUpdated;
    }
  }

  get comparisonIsPresenceBased(): boolean {
    const calculatedType = this.calculatedComparisonType;
    switch (calculatedType) {
      case ComparisonType.text:
        return [TextComparison.isBlank, TextComparison.isPresent].includes(this.comparison);
      case ComparisonType.singleChoice:
        return [SingleChoiceComparison.isBlank, SingleChoiceComparison.isPresent].includes(this.comparison);
      case ComparisonType.multiChoice:
        return [MultiChoiceComparison.isBlank, MultiChoiceComparison.isPresent].includes(this.comparison);
      case ComparisonType.numeric:
        return [NumericComparison.isBlank, NumericComparison.isPresent].includes(this.comparison);
      case ComparisonType.instanceCount:
        return [InstanceCountComparison.isBlank, InstanceCountComparison.isPresent].includes(this.comparison);
      case ComparisonType.datetime:
        return [DatetimeComparison.isBlank, DatetimeComparison.isPresent].includes(this.comparison);
      case ComparisonType.record:
        return [RecordComparison.isBlank, RecordComparison.isPresent].includes(this.comparison);
      case ComparisonType.boolean:
        return false;
      default:
        if (calculatedType !== null) {
          Logger.errorOnce('ConditionalRule#comparisonIsPresenceBased', `Unexpected type: ${calculatedType}`);
        }
        return false;
    }
  }

  get comparisonString(): string {
    switch (this.calculatedComparisonType) {
      case ComparisonType.instanceCount:
        switch (this.comparison) {
          case InstanceCountComparison.hasCountLessOrEqualTo:
            return 'has count less or equal to';
          case InstanceCountComparison.hasCountGreaterOrEqualTo:
            return 'has count greater or equal to';
          case InstanceCountComparison.hasCountLessThan:
            return 'has count less than';
          case InstanceCountComparison.hasCountGreaterThan:
            return 'has count greater than';
          case InstanceCountComparison.isUpdated:
            return 'is updated';
          case InstanceCountComparison.hadCountEqualTo:
            return 'had count equal to';
          case InstanceCountComparison.hadCountNotEqualTo:
            return 'had count not equal to';
          case InstanceCountComparison.isBlank:
            return 'is blank'
          case InstanceCountComparison.isPresent:
            return 'is present'
          case InstanceCountComparison.hasCountNotEqualTo:
            return 'has count not equal to';
          default:
            return 'has count equal to';
        }
      case ComparisonType.numeric:
        switch (this.comparison) {
          case NumericComparison.isGreaterOrEqualTo:
            return 'is greater or equal to';
          case NumericComparison.isLessOrEqualTo:
            return 'is less or equal to';
          case NumericComparison.isGreaterThan:
            return 'is greater than';
          case NumericComparison.isLessThan:
            return 'is less than';
          case NumericComparison.isNotEqualTo:
            return 'is not equal to';
          case NumericComparison.isUpdated:
            return 'is updated';
          case NumericComparison.was:
            return "was";
          case NumericComparison.wasNot:
            return "was not";
          case NumericComparison.isBlank:
            return 'is blank'
          case NumericComparison.isPresent:
            return 'is present'
          default:
            return 'is equal to';
        }
      case ComparisonType.singleChoice:
        switch (this.comparison) {
          case SingleChoiceComparison.isNot:
            return 'is not';
          case SingleChoiceComparison.isUpdated:
            return 'is updated';
          case SingleChoiceComparison.was:
            return 'was';
          case SingleChoiceComparison.wasNot:
            return 'was not';
          case SingleChoiceComparison.isBlank:
            return 'is blank'
          case SingleChoiceComparison.isPresent:
            return 'is present'
          default:
            return 'is';
        }
      case ComparisonType.multiChoice:
        switch (this.comparison) {
          case MultiChoiceComparison.doesNotInclude:
            return 'does not include';
          case MultiChoiceComparison.isUpdated:
            return 'is updated';
          case MultiChoiceComparison.isBlank:
            return 'is blank'
          case MultiChoiceComparison.isPresent:
            return 'is present'
          default:
            return 'includes';
        }
      case ComparisonType.datetime:
        switch (this.comparison) {
          case DatetimeComparison.isBefore:
            return 'is before';
          case DatetimeComparison.isAfter:
            return 'is after';
          case DatetimeComparison.isUpdated:
            return 'is updated';
          case DatetimeComparison.was:
            return 'was';
          case DatetimeComparison.wasNot:
            return 'was not';
          case DatetimeComparison.isWithin:
            return 'is within';
          case DatetimeComparison.isBlank:
            return 'is blank'
          case DatetimeComparison.isPresent:
            return 'is present'
          default:
            return 'is';
        }
      case ComparisonType.accountRelation:
        switch (this.comparison) {
          case AccountRelationComparison.is:
            return 'is';
          default:
            return '';
        }
      case ComparisonType.record:
        switch (this.comparison) {
          case RecordComparison.isBlank:
            return 'is blank'
          case RecordComparison.isPresent:
            return 'is present'
          default:
            return ''
        }
      case ComparisonType.boolean:
        switch (this.comparison) {
          case BooleanComparison.is:
            return 'is'
          case BooleanComparison.isNot:
            return 'is not'
          default:
            return ''
        }
      case ComparisonType.accountRelationUser:
        switch (this.comparison) {
          case AccountRelationUserComparison.is:
            return 'is';
          case AccountRelationUserComparison.isNot:
            return 'is not';
          default:
            return '';
        }
      default:
        switch (this.comparison) {
          case TextComparison.is:
            return 'is';
          case TextComparison.isNot:
            return 'is not';
          case TextComparison.contains:
            return 'contains';
          case TextComparison.doesNotContain:
            return 'does not contain';
          case TextComparison.startsWith:
            return 'starts with';
          case TextComparison.doesNotStartWith:
            return 'does not start with';
          case TextComparison.endsWith:
            return 'ends with';
          case TextComparison.doesNotEndWith:
            return 'does not end with';
          case TextComparison.isUpdated:
            return 'is updated';
          case TextComparison.was:
            return 'was';
          case TextComparison.wasNot:
            return 'was not';
          case TextComparison.isBlank:
            return 'is blank'
          case TextComparison.isPresent:
            return 'is present'
          default:
            return '';
        }
    }
  }

  get hasChoiceComparisonOptionValue(): boolean {
    return [ComparisonType.multiChoice, ComparisonType.singleChoice].includes(this.calculatedComparisonType);
  }

  valueString(form = null): string {
    if (!this.isValueRequired){
      return ''
    }

    if (this.is_reference) {
      if (!this.value) return ""
      // using form element repo causes circular dependency issue

      if (form) {
        const referencedFormElement = form.findElementContainer(this.value)
        return referencedFormElement?.label_title
      }
      return this.value
    }

    switch (this.calculatedComparisonType) {
      case ComparisonType.relativeUser:
      case ComparisonType.multiChoice:
      case ComparisonType.singleChoice:
        // payment builtin value types currently do not use references and can just return value
        if (this.builtInValueType && this.builtInValueType === ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod && this.value) return StringHelper.titleize(this.value)
        return this.getValueString(this.value);
      case ComparisonType.datetime:
        if (this.comparison === DatetimeComparison.isWithin) {
          const amount = this.value?.toString() || ''
          const unit = DurationAndTimeHelper.durationUnitOptions.find((option) => {
            return option.value === this.value_duration_unit
          })?.label || ''

          return `${amount} ${unit}`
        } else if (this.isRelativeDateRule) {
          return `${this.relative_date_type} ${this.relative_date}`
        } else {
          const reference = this.referencedControl || this.builtInReferencedValue;
          return DateHelper.toDisplay(this.value, { mode: reference?.getConditionDateTimeMode(), timezone: Selected.organization.get().time_zone })
        }
      case ComparisonType.accountRelation:
        const relationOption = ObjectModelRelationDefinition.accountRelationConditionalValueOptions().find(o => {
          return NumberHelper.toIntOrNull(o.value) === NumberHelper.toIntOrNull(this.value);
        });
        if (relationOption) {
          return relationOption.label;
        } else {
          return "";
        }
      case ComparisonType.accountRelationUser:
        if (this.value === "relative_user") {
          if (this.rootModel instanceof  ListView){
            return "Current User";
          }else{
            return "Relative User";
          }
        } else {
          return this.value || "";
        }
      default:
        if (this.value === null || this.value === undefined) {
          return '';
        } else {
          return this.value;
        }
    }
  }

  get rootName(): string {
    return 'conditional_rules';
  }

  get elementName(): string {
    return 'conditional_rule';
  }

  delete(): void {
    if (this.parent instanceof ConditionalRuleSet && this.parent.hasOne) {
      this.parent.destroy();
    } else {
      super.destroy();
    }
  }

  get isValueRequired(): boolean {
    return !this.comparisonIsUpdated && !this.comparisonIsPresenceBased
  }

  get builtInReferencedValue(): BuiltInReferencedValue {
    const type = this.builtInValueType;
    if (!type) return null;

    return new BuiltInReferencedValue(type, this.rootModel);
  }

  get builtInValueType(): ConditionalRuleBuiltInValueType {
    if (!this.referenced_control_id) return null;

    return typeForConditionalRuleBuiltInValue(this.referenced_control_id);
  }

}